import React from 'react';
import ReactDOM from 'react-dom';
import { RecommendationsWidget } from '../recommendations-widget';
import { OffersWidget } from '../offers-widget';
import { SurfaceRecs, SurfaceType } from './surfaces';

/**
 * Portable CDP recommendations Web Component — brand-agnostic, config-driven, and
 * decoupled from Umbraco. The migration target for the macro-based widgets: instead
 * of per-widget C# scaffolding (ModelsBuilder + Mediator handler + doctype + dispatch
 * case) rendered server-side via @Html.React, any surface embeds one script + tag:
 *
 *   <script src="https://<cdn>/dist/js/cdp-recs.bundle.js"></script>
 *   <cdp-recs type="models" heading="You might also like"
 *             context="outback" api-base="https://ai-api.inchcape-web.au"></cdp-recs>
 *
 * Attributes (all optional): `type` (models|offers, default models), `heading`, `count`,
 * `context` (the current model slug), `api-base`. Identity, consent and reactivity come
 * from the neutral window hooks the inner widgets already read — `window.CdpUserId`,
 * `window.CdpRecsConsent()` and the `CdpRecsSignal` re-fetch event — so there is no
 * per-brand server code. Renders into light DOM so the site's stylesheet applies (the
 * self-contained shadow-DOM + inlined-CSS variant is a follow-up for non-Umbraco hosts).
 *
 * IMPORTANT: the custom-element class is declared LAZILY inside defineCdpRecsElement(),
 * not at module top level. This module is also compiled into the server-side SSR bundle
 * (ssr.bundle.js), and `class extends HTMLElement` evaluates `HTMLElement` at definition
 * time — which does not exist in the SSR JS engine. Declaring it at top level throws
 * `ReferenceError: HTMLElement is not defined` when the SSR engine loads the module,
 * poisoning the engine so every server-rendered page 500s. Keeping the class inside a
 * browser-guarded function makes this module import-safe under SSR.
 */
export function defineCdpRecsElement(): void {
  // SSR / non-browser: no HTMLElement, no custom-element registry — do nothing.
  if (
    typeof window === 'undefined' ||
    typeof HTMLElement === 'undefined' ||
    typeof window.customElements === 'undefined'
  ) {
    return;
  }
  // Idempotent — a page can include the bundle more than once without throwing.
  if (window.customElements.get('cdp-recs')) {
    return;
  }

  class CdpRecsElement extends HTMLElement {
    private mountEl: HTMLDivElement | null = null;

    static get observedAttributes(): string[] {
      return [
        'type',
        'heading',
        'count',
        'context',
        'api-base',
        'variant',
        'stage',
      ];
    }

    connectedCallback(): void {
      if (!this.mountEl) {
        this.mountEl = document.createElement('div');
        this.appendChild(this.mountEl);
      }
      this.renderWidget();
    }

    attributeChangedCallback(): void {
      if (this.mountEl) this.renderWidget();
    }

    disconnectedCallback(): void {
      if (this.mountEl) {
        ReactDOM.unmountComponentAtNode(this.mountEl);
        this.mountEl = null;
      }
    }

    private renderWidget(): void {
      if (!this.mountEl) return;
      const type = (this.getAttribute('type') || 'models').toLowerCase();
      const heading = this.getAttribute('heading') || undefined;
      const countAttr = this.getAttribute('count');
      const count =
        countAttr && !Number.isNaN(parseInt(countAttr, 10))
          ? parseInt(countAttr, 10)
          : undefined;
      const apiBase = this.getAttribute('api-base') || undefined;
      const context = this.getAttribute('context') || undefined;
      const variant = this.getAttribute('variant') || undefined;
      const stage = this.getAttribute('stage') || undefined;

      // type routing: offers -> OffersWidget; the context/deterministic + CTA surfaces
      // -> SurfaceRecs; everything else (default 'models') -> the personalized widget.
      const SURFACE_TYPES = [
        'complementary',
        'recently-viewed',
        'popular',
        'related',
        'next-best-action',
      ];
      let widget: React.ReactElement;
      if (type === 'offers') {
        widget = (
          <OffersWidget heading={heading} count={count} apiBase={apiBase} />
        );
      } else if (SURFACE_TYPES.indexOf(type) !== -1) {
        widget = (
          <SurfaceRecs
            surface={type as SurfaceType}
            heading={heading}
            count={count}
            apiBase={apiBase}
            modelSlug={context}
            variant={variant}
            stage={stage}
          />
        );
      } else {
        widget = (
          <RecommendationsWidget
            heading={heading}
            count={count}
            apiBase={apiBase}
            modelSlug={context}
          />
        );
      }
      ReactDOM.render(widget, this.mountEl);
    }
  }

  window.customElements.define('cdp-recs', CdpRecsElement);
}
